import classNames from "classnames";
import React, { useRef, useState } from "react";
import { Icon } from "../../graphic/Icon";
import styles from "./SiteStoryBox.scss";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// How much of a dragged box stays on screen sideways, so it can be grabbed again
const DRAG_VISIBLE = 64;

/**
 * Box for app content in the interactive stories on the public pages, like a cut-out of the app.
 * The content gets the app's own text style. Place boxes on a SiteStoryStage (e.g. positioned with `className`).
 * @param title – header row
 * @param icon – icon before the title, e.g. the module's icon
 * @param iconVariant – "fill": white on primary, like the module list (default); "border": primary with a subtle
 * border, like the product menu
 * @param layer – 1, 2 or 3: how far up the box lies. Higher layers lie on top and have a stronger blue border.
 * @param draggable – the box can be dragged by its title, anywhere on the page. It may go past the sides of the window,
 * but a part of it stays on screen.
 * @param closable – shows a close button in the header, which calls `onClose` (the story decides what closing means)
 * @param closeLabel – accessible label of the close button
 * @param onMove – called while the box is dragged, e.g. to let the story know it has been changed
 * @param toast – a SiteStoryToast, shown just below the box (overlapping it a little) and moving along with it
 * @param className – positions the box
 */
export const SiteStoryBox = ({
    title,
    icon,
    iconVariant = "fill",
    layer = 1,
    draggable,
    closable,
    onClose,
    closeLabel,
    onMove,
    toast,
    className,
    children
}) => {
    const boxRef = useRef(null);
    const dragRef = useRef(null);
    const [offset, setOffset] = useState({ x: 0, y: 0 });

    const handlePointerDown = (event) => {
        if (event.button !== 0 || event.target.closest("button")) {
            return;
        }
        event.currentTarget.setPointerCapture(event.pointerId);
        // Horizontal limits that keep a part of the box on screen (the page doesn't scroll sideways to find it)
        const boxRect = boxRef.current.getBoundingClientRect();
        dragRef.current = {
            startX: event.clientX - offset.x,
            startY: event.clientY - offset.y,
            minX: offset.x - (boxRect.right - DRAG_VISIBLE),
            maxX: offset.x + (document.documentElement.clientWidth - DRAG_VISIBLE - boxRect.left)
        };
    };

    const handlePointerMove = (event) => {
        const drag = dragRef.current;
        if (!drag) {
            return;
        }
        setOffset({
            x: clamp(event.clientX - drag.startX, drag.minX, drag.maxX),
            y: event.clientY - drag.startY
        });
        onMove?.();
    };

    const handlePointerUp = () => {
        dragRef.current = null;
    };

    return (
        <div
            ref={boxRef}
            className={classNames(styles.root, styles[`layer${layer}`], className)}
            style={draggable ? { transform: `translate(${offset.x}px, ${offset.y}px)` } : null}
        >
            <div className={styles.base}>
                {title ? (
                    <div
                        className={classNames(styles.head, { [styles.handle]: draggable })}
                        {...(draggable
                            ? {
                                  onPointerDown: handlePointerDown,
                                  onPointerMove: handlePointerMove,
                                  onPointerUp: handlePointerUp,
                                  onPointerCancel: handlePointerUp
                              }
                            : {})}
                    >
                        {icon ? (
                            <span
                                className={classNames(styles.icon, { [styles.iconBorder]: iconVariant === "border" })}
                            >
                                <Icon name={icon} />
                            </span>
                        ) : null}
                        <div className={styles.title}>{title}</div>
                        {closable ? (
                            <button type="button" className={styles.close} aria-label={closeLabel} onClick={onClose}>
                                <Icon name="close" />
                            </button>
                        ) : null}
                    </div>
                ) : null}
                {children}
            </div>
            {toast}
        </div>
    );
};
